// src/components/messaging/AudioPlayer.tsx
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Box, IconButton, Typography, alpha } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import MicIcon from '@mui/icons-material/Mic';

interface AudioPlayerProps {
  src: string;
  isOwn?: boolean;
  duration?: number | null;
}

// Generate deterministic waveform bars based on src hash
const generateWaveformBars = (src: string, count: number = 28): number[] => {
  let hash = 0;
  for (let i = 0; i < src.length; i++) {
    hash = ((hash << 5) - hash) + src.charCodeAt(i);
    hash = hash & hash;
  }
  const bars: number[] = [];
  for (let i = 0; i < count; i++) {
    hash = ((hash << 5) - hash) + i;
    const height = 20 + Math.abs(hash % 80); // 20-100% height
    bars.push(height);
  }
  return bars;
};

const AudioPlayer: React.FC<AudioPlayerProps> = ({ src, isOwn = false, duration: initialDuration }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(initialDuration || 0);
  const [currentTime, setCurrentTime] = useState(0);
  const [, setIsReady] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Generate waveform bars once based on src
  const waveformBars = useMemo(() => generateWaveformBars(src), [src]);

  // Format time as M:SS
  const formatTime = (seconds: number): string => {
    if (!isFinite(seconds) || isNaN(seconds) || seconds === 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate progress percentage
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Toggle play/pause
  const togglePlayback = useCallback(async () => {
    if (!audioRef.current) return;

    try {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        await audioRef.current.play();
        setIsPlaying(true);
      }
    } catch (err) {
      console.error('Audio playback error:', err);
    }
  }, [isPlaying]);

  // Handle waveform click for seeking
  const handleWaveformClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!audioRef.current || !duration) return;

      const rect = event.currentTarget.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const percentage = clickX / rect.width;
      const newTime = percentage * duration;

      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    },
    [duration]
  );

  // Initialize audio element
  useEffect(() => {
    if (!src) return;

    const audio = new Audio();
    audioRef.current = audio;
    audio.preload = 'metadata';

    audio.onloadedmetadata = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
      setIsReady(true);
    };

    audio.oncanplay = () => {
      setIsReady(true);
    };

    audio.onended = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.ontimeupdate = () => {
      setCurrentTime(audio.currentTime);
    };

    audio.src = src;
    audio.load();

    return () => {
      audio.pause();
      audio.src = '';
      audioRef.current = null;
    };
  }, [src]);

  // Update duration if provided from backend
  useEffect(() => {
    if (initialDuration && initialDuration > 0) {
      setDuration(initialDuration);
    }
  }, [initialDuration]);

  // Colors based on ownership
  const accentColor = isOwn ? 'white' : 'primary.main';
  const mutedColor = isOwn ? 'rgba(255,255,255,0.6)' : 'text.secondary';
  const barBgColor = isOwn ? 'rgba(255,255,255,0.3)' : alpha('#1976d2', 0.2);
  const barActiveColor = isOwn ? 'white' : 'primary.main';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.5,
        minWidth: 180,
        maxWidth: 260,
      }}
    >
      {/* Mic icon */}
      <MicIcon
        sx={{
          color: accentColor,
          fontSize: 18,
          opacity: 0.8,
        }}
      />

      {/* Play/Pause button */}
      <IconButton
        onClick={togglePlayback}
        size="small"
        sx={{
          backgroundColor: isOwn ? 'rgba(255,255,255,0.2)' : alpha('#1976d2', 0.1),
          color: accentColor,
          width: 32,
          height: 32,
          '&:hover': {
            backgroundColor: isOwn ? 'rgba(255,255,255,0.3)' : alpha('#1976d2', 0.2),
          },
        }}
      >
        {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
      </IconButton>

      {/* Waveform */}
      <Box
        onClick={handleWaveformClick}
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
          height: 28,
          cursor: 'pointer',
          px: 0.5,
        }}
      >
        {waveformBars.map((height, index) => {
          const barProgress = (index / waveformBars.length) * 100;
          const isActive = barProgress <= progress;
          return (
            <Box
              key={index}
              sx={{
                width: 3,
                height: `${height}%`,
                borderRadius: 1,
                backgroundColor: isActive ? barActiveColor : barBgColor,
                transition: 'background-color 0.1s',
              }}
            />
          );
        })}
      </Box>

      {/* Duration */}
      <Typography
        variant="caption"
        sx={{
          color: mutedColor,
          fontSize: '0.7rem',
          fontFamily: 'monospace',
          minWidth: 32,
          textAlign: 'right',
        }}
      >
        {isPlaying || currentTime > 0 ? formatTime(currentTime) : formatTime(duration)}
      </Typography>
    </Box>
  );
};

export default AudioPlayer;
